import React, { FunctionComponent, ReactNode } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { getReflexivePronoun } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'

import { BodyText, Size as BodyTextSize } from '../../../atoms/bodyText/BodyText'
import { InfoIcon } from '../../../atoms/icons/InfoIcon'
import { Link } from '../../../atoms/link/Link'
import { SummaryDetailProps } from '../../../atoms/summaryDetail/SummaryDetail'
import { Tooltip } from '../../../atoms/tooltip/Tooltip'
import { InlineWarningBanner } from '../../../molecules/inlineWarningBanner/InlineWarningBanner'
import { ClientDetailsSidePanelDetail, ClientDetailsSidePanelDetails } from '../ClientDetailsSidePanelDetails'
import { ClientDetailsSidePanelSection } from '../ClientDetailsSidePanelSection'
import { sponsoredBenefitsSidePanelMessages } from './SponsoredBenefitsSidePanel.messages'

export type SponsoredBenefitsSidePanelVariant =
  | 'active'
  | 'runningLow'
  | 'outOfSessions'
  | 'selfPayActive'
  | 'healthPlan'
  | 'eligibilityNotConfirmed'
  | 'noCoverage'

export type SponsoredBenefitsSidePanelData = {
  sponsorName: string
  clientFirstName: string
  benefitsResetDate: string
  details: readonly SummaryDetailProps[]
  variant: SponsoredBenefitsSidePanelVariant
  sessionLimitCategory?: string
  selfPayStatus?: 'active' | 'eligible'
  clientPronouns?: string[]
  additionalInfo?: ReactNode
  warningBanner?: ReactNode
  isLyraSelect: boolean
  eligibilityAssistance: 'careNavigationTeam' | 'helpCenter'
}

export type SponsoredBenefitsSidePanelAction =
  | 'viewServicesAndBenefits'
  | 'learnMoreAboutLyraSelect'
  | 'submitEligibilitySupportTicket'

export type SponsoredBenefitsSidePanelSectionProps = {
  data?: SponsoredBenefitsSidePanelData
  defaultExpanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  onActionPress: (action: SponsoredBenefitsSidePanelAction) => void
  showViewServicesAndBenefitsLink?: boolean
}

const ResetDate = styled(BodyText)(({ theme }) => ({
  marginTop: theme.spacing['2px'],
}))

const AdditionalInfo = styled(BodyText)(({ theme }) => ({
  marginTop: theme.spacing['16px'],
}))

const NoticeList = styled.View(({ theme }) => ({
  width: '100%',
  gap: theme.spacing['16px'],
}))

const InlineText = styled(BodyText)`
  display: contents;
`

const SelfPayTooltipAnchor = styled.View(({ theme }) => ({
  marginLeft: theme.spacing['4px'],
}))

export const SponsoredBenefitsSidePanelSection: FunctionComponent<SponsoredBenefitsSidePanelSectionProps> = ({
  data,
  defaultExpanded = true,
  onExpandedChange,
  onActionPress,
  showViewServicesAndBenefitsLink = false,
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()
  const subtitle = formatMessage(sponsoredBenefitsSidePanelMessages.subtitle)

  if (!data) {
    return (
      <ClientDetailsSidePanelSection
        subtitle={subtitle}
        accessibilityName={subtitle}
        header={{ kind: 'static', title: formatMessage(sponsoredBenefitsSidePanelMessages.loadingTitle) }}
        testIDPrefix='SponsoredBenefits'
      />
    )
  }

  const notices: ReactNode[] = []
  if (data.isLyraSelect) {
    notices.push(
      <InlineWarningBanner
        key='lyra-select'
        testId='SponsoredBenefits-lyraSelect-notice'
        title={formatMessage(sponsoredBenefitsSidePanelMessages.lyraSelectTitle)}
        text={formatMessage(sponsoredBenefitsSidePanelMessages.lyraSelectNotice, {
          clientFirstName: data.clientFirstName,
        })}
        trailingLink={
          <Link
            text={formatMessage(sponsoredBenefitsSidePanelMessages.learnMoreAboutLyraSelect)}
            size={BodyTextSize.SMALL}
            color={colors.textWarning}
            underline
            onPress={() => onActionPress('learnMoreAboutLyraSelect')}
          />
        }
        textSize={BodyTextSize.SMALL}
        textColor={colors.textWarning}
        iconColor={colors.textWarning}
        marginBottom='0px'
      />,
    )
  }
  if (data.warningBanner) {
    notices.push(<React.Fragment key='hpi-self-pay'>{data.warningBanner}</React.Fragment>)
  }
  if (data.variant === 'eligibilityNotConfirmed') {
    const isHelpCenter = data.eligibilityAssistance === 'helpCenter'
    notices.push(
      <InlineWarningBanner
        key='eligibility'
        testId='SponsoredBenefits-eligibility-notice'
        text={formatMessage(
          isHelpCenter
            ? sponsoredBenefitsSidePanelMessages.eligibilityNotConfirmedHelpCenterNotice
            : sponsoredBenefitsSidePanelMessages.eligibilityNotConfirmedCareNavigationNotice,
          {
            clientFirstName: data.clientFirstName,
            bold: (chunks: ReactNode) => (
              <InlineText key='bold' size={BodyTextSize.SMALL} color={colors.textWarning} bold text={chunks} />
            ),
            link: (chunks: ReactNode) => (
              <Link
                key='link'
                text={chunks}
                size={BodyTextSize.SMALL}
                color={colors.textWarning}
                underline
                onPress={() => onActionPress('submitEligibilitySupportTicket')}
              />
            ),
          },
        )}
        textSize={BodyTextSize.SMALL}
        textColor={colors.textWarning}
        iconColor={colors.textWarning}
        marginBottom='0px'
      />,
    )
  }

  const persistentContent = notices.length ? <NoticeList>{notices}</NoticeList> : undefined
  const footer = showViewServicesAndBenefitsLink ? (
    <Link
      text={formatMessage(sponsoredBenefitsSidePanelMessages.viewServicesAndBenefits)}
      textAlign='center'
      onPress={() => onActionPress('viewServicesAndBenefits')}
    />
  ) : undefined

  if (data.variant === 'eligibilityNotConfirmed' || data.variant === 'noCoverage') {
    return (
      <ClientDetailsSidePanelSection
        subtitle={subtitle}
        accessibilityName={subtitle}
        header={{
          kind: 'static',
          title: formatMessage(
            data.variant === 'eligibilityNotConfirmed'
              ? sponsoredBenefitsSidePanelMessages.eligibilityNotConfirmedTitle
              : sponsoredBenefitsSidePanelMessages.noCoverageTitle,
          ),
          titleTone: data.variant === 'eligibilityNotConfirmed' ? 'warning' : undefined,
        }}
        persistentContent={persistentContent}
        footer={footer}
        testIDPrefix='SponsoredBenefits'
      />
    )
  }

  const isWarningVariant = data.variant === 'runningLow' || data.variant === 'outOfSessions'
  // With several per-program rows, match on category so an unrelated healthy row is not
  // flagged. A single row is unambiguous; so is an unresolvable category (programs such as
  // manager coaching map to no session-limit category), where the first row is the best
  // available guess and is preferable to highlighting nothing.
  const canMatchOnCategory = data.details.length > 1 && data.sessionLimitCategory !== undefined
  const isEpisodeRow = (detail: SummaryDetailProps, index: number) =>
    canMatchOnCategory ? detail.programCategory === data.sessionLimitCategory : index === 0

  const details: ClientDetailsSidePanelDetail[] = data.details.map((detail, index) => {
    const isWarning = detail.highlightValueAsWarning || (isWarningVariant && isEpisodeRow(detail, index))
    return {
      id: `detail-${index}`,
      label: detail.label,
      sublabel: detail.sublabel,
      value: detail.value,
      valueTone: isWarning ? 'warning' : undefined,
      valueEmphasis: isWarning ? 'prominent' : 'default',
    }
  })

  if (data.selfPayStatus) {
    details.push({
      id: 'self-pay',
      label: formatMessage(sponsoredBenefitsSidePanelMessages.selfPayLabel),
      labelAccessory: (
        <SelfPayTooltipAnchor>
          <Tooltip
            hoverEnabled
            placement='top'
            content={formatMessage(sponsoredBenefitsSidePanelMessages.selfPayTooltip, {
              clientFirstName: data.clientFirstName,
              reflexivePronoun: getReflexivePronoun(data.clientPronouns),
            })}
            accessibilityLabel={formatMessage(sponsoredBenefitsSidePanelMessages.selfPayTooltipAccessibilityLabel)}
          >
            <InfoIcon size={16} fillColor={colors.iconActive} />
          </Tooltip>
        </SelfPayTooltipAnchor>
      ),
      value: formatMessage(
        data.selfPayStatus === 'active'
          ? sponsoredBenefitsSidePanelMessages.activeValue
          : sponsoredBenefitsSidePanelMessages.eligibleValue,
      ),
      valueTone: data.selfPayStatus === 'active' ? 'success' : undefined,
    })
  }

  const content = (
    <>
      {data.benefitsResetDate ? (
        <ResetDate
          size={BodyTextSize.CAPTION}
          color={colors.textSecondary}
          text={formatMessage(sponsoredBenefitsSidePanelMessages.benefitsResetDate, {
            benefitsResetDate: data.benefitsResetDate,
          })}
          regular
        />
      ) : null}
      <ClientDetailsSidePanelDetails testIDPrefix='SponsoredBenefitsDetails' items={details} />
      {data.additionalInfo ? (
        <AdditionalInfo size={BodyTextSize.SMALL} color={colors.textSecondary} text={data.additionalInfo} regular />
      ) : null}
    </>
  )

  const primaryDetail = data.details?.[0]
  const defaultCollapsedTitle: ReactNode = primaryDetail ? (
    <>
      {primaryDetail.value} {primaryDetail.label}
    </>
  ) : (
    formatMessage(
      data.selfPayStatus === 'eligible'
        ? sponsoredBenefitsSidePanelMessages.selfPayEligibleTitle
        : sponsoredBenefitsSidePanelMessages.benefitsAvailableTitle,
    )
  )
  const variantCollapsedTitles: Partial<Record<SponsoredBenefitsSidePanelVariant, ReactNode>> = {
    outOfSessions: formatMessage(sponsoredBenefitsSidePanelMessages.noSessionsRemainingTitle),
    selfPayActive: formatMessage(sponsoredBenefitsSidePanelMessages.selfPayTitle),
    healthPlan: formatMessage(sponsoredBenefitsSidePanelMessages.healthPlanTitle),
  }
  const collapsedTitle = variantCollapsedTitles[data.variant] ?? defaultCollapsedTitle

  return (
    <ClientDetailsSidePanelSection
      subtitle={subtitle}
      accessibilityName={subtitle}
      header={{
        kind: 'collapsible',
        collapsedTitle,
        expandedTitle: data.sponsorName,
        collapsedTitleTone: isWarningVariant ? 'warning' : undefined,
        defaultExpanded,
        onExpandedChange,
      }}
      persistentContent={persistentContent}
      content={content}
      footer={footer}
      testIDPrefix='SponsoredBenefits'
    />
  )
}
